'use client';

import { getGlobalInitData } from '@/api/public';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { HOME_PREVIEW_PATH } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { isPcNavDraftPreview } from '@/utils/pc-nav-preview';
import {
  buildGlobalInitRequestPayload,
  mapGlobalInitResponseToChromeState,
  unwrapGlobalInitResponse,
} from '@/utils/map-global-init-data';
import { useEffect, useRef } from 'react';

/** 首屏沿用服务端数据；仅在 token 变化后刷新公共资源和合规策略。 */
export function usePublicResourceRefresh(deviceType: EPlatform) {
  const token = useStore((state) => state.token);
  const setPublicResourceData = useStore((state) => state.setPublicResourceData);
  const setCompliancePolicies = useStore((state) => state.setCompliancePolicies);
  const previousToken = useRef(token);

  useEffect(() => {
    if (previousToken.current === token) return;
    previousToken.current = token;

    let ignore = false;
    const refresh = async () => {
      try {
        const isWeb = deviceType === EPlatform.WEB;
        const pathname = window.location.pathname;
        const previewType = new URLSearchParams(window.location.search).get('type');
        // 沿用旧 useGlobalShellInit 的首页预览和 App store 规则。
        const isHomePreview = pathname.includes(HOME_PREVIEW_PATH);
        let storeId = isWeb ? EPlatform.WEB : EPlatform.WAP;
        if (isHomePreview && previewType === '3') {
          storeId = EPlatform.IOS;
        }
        const navMode = isPcNavDraftPreview({ isWeb, pathname, previewType })
          ? 'pc-header-draft'
          : 'published';
        const response = await getGlobalInitData(
          isWeb,
          buildGlobalInitRequestPayload(isHomePreview, { navMode }),
          { storeIdOverride: storeId },
        );
        if (ignore || !response?.data) return;

        const next = mapGlobalInitResponseToChromeState(unwrapGlobalInitResponse(response), isWeb);
        setPublicResourceData(next.publicResourceData);
        setCompliancePolicies(next.compliancePolicies ?? null);
      } catch (error) {
        if (!ignore) {
          logger.error(error, 'usePublicResourceRefresh: getGlobalInitData failed');
        }
      }
    };

    void refresh();
    return () => {
      // 换号或卸载后，旧请求不能覆盖当前用户的公共资源。
      ignore = true;
    };
  }, [token, deviceType, setPublicResourceData, setCompliancePolicies]);
}
